import FilesEntity from '@/entities/api/files/files'
import { API_URL } from '@/services/api/keys'
import {
	ChevronLeft,
	ChevronRight,
	X,
	ZoomIn,
	ZoomOut,
	RotateCw,
	Download,
} from 'lucide-react'
import { useEffect, useState, useCallback } from 'react'

interface ProductImageModalProps {
	isOpen: boolean
	onClose: () => void
	images: FilesEntity[]
	currentIndex: number
	isMobile: boolean
	onImageClick?: (index: number) => void
}

const ProductImageModal: React.FC<ProductImageModalProps> = ({
	isOpen,
	onClose,
	images,
	currentIndex,
	isMobile,
	onImageClick,
}) => {
	const [activeIndex, setActiveIndex] = useState(currentIndex)
	const [zoomLevel, setZoomLevel] = useState(1)
	const [rotation, setRotation] = useState(0)
	const [isDragging, setIsDragging] = useState(false)
	const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
	const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 })
	const [isLoading, setIsLoading] = useState(false)

	useEffect(() => {
		setActiveIndex(currentIndex)
	}, [currentIndex])

	useEffect(() => {
		if (isOpen) {
			setZoomLevel(1)
			setRotation(0)
			setImagePosition({ x: 0, y: 0 })
		}
	}, [activeIndex, isOpen])

	const handleKeyDown = useCallback(
		(e: KeyboardEvent) => {
			if (!isOpen) return

			switch (e.key) {
				case 'Escape':
					onClose()
					break
				case 'ArrowLeft':
					setActiveIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1))
					break
				case 'ArrowRight':
					setActiveIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1))
					break
			}
		},
		[isOpen, onClose, images.length]
	)

	useEffect(() => {
		document.addEventListener('keydown', handleKeyDown)
		return () => document.removeEventListener('keydown', handleKeyDown)
	}, [handleKeyDown])

	const handleZoomIn = () => {
		setZoomLevel((prev) => Math.min(prev + 0.5, 5))
	}

	const handleZoomOut = () => {
		setZoomLevel((prev) => Math.max(prev - 0.5, 0.5))
	}

	const handleRotate = () => {
		setRotation((prev) => (prev + 90) % 360)
	}

	const handleDownload = () => {
		const link = document.createElement('a')
		link.href = `${API_URL}/${images[activeIndex]?.url}`
		link.download = `producto-imagen-${activeIndex + 1}.jpg`
		document.body.appendChild(link)
		link.click()
		document.body.removeChild(link)
	}

	const handleMouseDown = (e: React.MouseEvent) => {
		if (zoomLevel > 1) {
			setIsDragging(true)
			setDragStart({
				x: e.clientX - imagePosition.x,
				y: e.clientY - imagePosition.y,
			})
		}
	}

	const handleMouseMove = (e: React.MouseEvent) => {
		if (isDragging && zoomLevel > 1) {
			setImagePosition({
				x: e.clientX - dragStart.x,
				y: e.clientY - dragStart.y,
			})
		}
	}

	const handleMouseUp = () => {
		setIsDragging(false)
	}

	const resetImage = () => {
		setZoomLevel(1)
		setRotation(0)
		setImagePosition({ x: 0, y: 0 })
	}

	const navigateImage = (direction: 'prev' | 'next') => {
		if (direction === 'prev') {
			setActiveIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1))
		} else {
			setActiveIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1))
		}
	}

	const handleImageClick = (index: number) => {
		setActiveIndex(index)
		if (onImageClick) {
			onImageClick(index)
		}
	}

	if (!isOpen) return null

	if (isMobile) {
		return (
			<div className='fixed inset-0 z-[9999999999999] bg-black bg-opacity-95 flex items-center justify-center overflow-auto'>
				<button
					onClick={onClose}
					className='fixed top-4 right-4 z-[9999999] p-2 rounded-full bg-black bg-opacity-60 hover:bg-opacity-80 text-white cursor-pointer  transition-colors'
				>
					<X size={24} />
				</button>
				

				<div className='relative w-full h-full flex items-center justify-center p-4'>
					<img
						src={
							`${API_URL}/${images[activeIndex]?.url}` ||
							'https://placehold.co/800x800'
						}
						alt={`Producto ${activeIndex + 1}`}
						className='max-w-full max-h-full object-contain'
						style={{
							transform: `scale(${zoomLevel}) rotate(${rotation}deg)`,
							transition: 'transform 0.3s ease',
						}}
					/>

					{images.length > 1 && (
						<>
							<button
								onClick={() => navigateImage('prev')}
								className='absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-70 transition-all'
							>
								<ChevronLeft size={24} />
							</button>
							<button
								onClick={() => navigateImage('next')}
								className='absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-70 transition-all'
							>
								<ChevronRight size={24} />
							</button>
						</>
					)}

					<div className='absolute bottom-20 left-1/2 transform -translate-x-1/2 flex space-x-4 bg-black bg-opacity-50 rounded-full px-4 py-2'>
						<button
							onClick={handleZoomOut}
							className='text-white hover:text-red-400 transition-colors'
						>
							<ZoomOut size={20} />
						</button>
						<button
							onClick={handleZoomIn}
							className='text-white hover:text-red-400 transition-colors'
						>
							<ZoomIn size={20} />
						</button>
						<button
							onClick={handleRotate}
							className='text-white hover:text-red-400 transition-colors'
						>
							<RotateCw size={20} />
						</button>
						<button
							onClick={handleDownload}
							className='text-white hover:text-red-400 transition-colors'
						>
							<Download size={20} />
						</button>
					</div>

					<div className='absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2'>
						{images.map((_, index) => (
							<button
								key={index}
								onClick={() => handleImageClick(index)}
								className={`w-3 h-3 rounded-full transition-all ${
									index === activeIndex
										? 'bg-red-600'
										: 'bg-white bg-opacity-50 hover:bg-opacity-75'
								}`}
							/>
						))}
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className='absolute inset-0 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden z-50 transition-all duration-300 transform scale-100 opacity-100'>
			<button
				onClick={onClose}
				className='fixed top-14 right-4 z-[9999999] p-2 rounded-full bg-black bg-opacity-60 hover:bg-opacity-80 cursor-pointer text-white  shadow-lg transition-colors'
			>
				<X size={24} />
			</button>

			<div className='h-full flex flex-col'>
				<div className='flex items-center justify-between p-2 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white'>
					<div className='flex items-center space-x-2'>
						<span className='text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full'>
							{activeIndex + 1} de {images.length}
						</span>
					</div>

					<div className='flex items-center space-x-1'>
						<div className='flex items-center space-x-1 bg-gray-100 rounded-lg p-1'>
							<button
								onClick={handleZoomOut}
								disabled={zoomLevel <= 0.5}
								className='p-1 rounded-md hover:bg-white transition-all text-gray-600 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed'
								title='Alejar'
							>
								<ZoomOut size={12} />
							</button>
							<span className='text-xs font-medium text-gray-600 px-1 min-w-[2.5rem] text-center'>
								{Math.round(zoomLevel * 100)}%
							</span>
							<button
								onClick={handleZoomIn}
								disabled={zoomLevel >= 5}
								className='p-1 rounded-md hover:bg-white transition-all text-gray-600 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed'
								title='Acercar'
							>
								<ZoomIn size={12} />
							</button>
						</div>

						<button
							onClick={handleRotate}
							className='p-1 rounded-md bg-gray-100 hover:bg-white transition-all text-gray-600 hover:text-red-600'
							title='Rotar'
						>
							<RotateCw size={12} />
						</button>

						<button
							onClick={handleDownload}
							className='p-1 rounded-md bg-gray-100 hover:bg-white transition-all text-gray-600 hover:text-red-600'
							title='Descargar'
						>
							<Download size={12} />
						</button>
					</div>
				</div>

				<div
					className='flex-1 relative overflow-hidden bg-gradient-to-br from-gray-50 via-white to-gray-100'
					onMouseDown={handleMouseDown}
					onMouseMove={handleMouseMove}
					onMouseUp={handleMouseUp}
					onMouseLeave={handleMouseUp}
				>
					<div className='absolute inset-0 flex items-center justify-center'>
						<img
							src={
								`${API_URL}/${images[activeIndex]?.url}` ||
								'https://placehold.co/800x800'
							}
							alt={`Producto ${activeIndex + 1}`}
							className='max-w-full max-h-full object-contain select-none transition-transform duration-300 ease-out'
							style={{
								transform: `scale(${zoomLevel}) rotate(${rotation}deg) translate(${
									imagePosition.x / zoomLevel
								}px, ${imagePosition.y / zoomLevel}px)`,
								cursor:
									zoomLevel > 1
										? isDragging
											? 'grabbing'
											: 'grab'
										: 'default',
							}}
							onLoad={() => setIsLoading(false)}
							onLoadStart={() => setIsLoading(true)}
							draggable={false}
						/>
					</div>

					{isLoading && (
						<div className='absolute inset-0 flex items-center justify-center bg-white bg-opacity-75'>
							<div className='animate-spin rounded-full h-6 w-6 border-b-2 border-red-600'></div>
						</div>
					)}

					{images.length > 1 && (
						<>
							<button
								onClick={() => navigateImage('prev')}
								className='absolute left-2 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-90 hover:bg-opacity-100 text-gray-700 p-2 rounded-full shadow-md hover:shadow-lg transition-all hover:scale-105 focus:outline-none focus:ring-2 focus:ring-red-500'
								title='Imagen anterior'
							>
								<ChevronLeft size={16} />
							</button>
							<button
								onClick={() => navigateImage('next')}
								className='absolute right-2 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-90 hover:bg-opacity-100 text-gray-700 p-2 rounded-full shadow-md hover:shadow-lg transition-all hover:scale-105 focus:outline-none focus:ring-2 focus:ring-red-500'
								title='Siguiente imagen'
							>
								<ChevronRight size={16} />
							</button>
						</>
					)}

					{(zoomLevel > 1 || rotation !== 0) && (
						<button
							onClick={resetImage}
							className='absolute bottom-2 right-2 bg-white bg-opacity-90 hover:bg-opacity-100 text-gray-700 px-2 py-1 rounded-full text-xs font-medium shadow-md hover:shadow-lg transition-all'
						>
							Reset
						</button>
					)}
				</div>

				{images.length > 1 && (
					<div className='border-t border-gray-200 bg-white p-2'>
						<div className='flex space-x-1 overflow-hidden p-4 gap-2 scrollbar-hide'>
							{images.map((image, index) => (
								<button
									key={index}
									onClick={() => handleImageClick(index)}
									onMouseEnter={() => {}}
									className={`flex-shrink-0 relative overflow-hidden rounded-md transition-all duration-200 transform hover:scale-110 ${
										index === activeIndex
											? 'ring-2 ring-red-600 shadow-md scale-105'
											: 'ring-1 ring-gray-200 hover:ring-red-400 hover:shadow-sm opacity-70 hover:opacity-100'
									}`}
								>
									<img
										src={
											`${API_URL}/${image.url}` || 'https://placehold.co/40x40'
										}
										alt={`Vista previa ${index + 1}`}
										className='w-8 h-8 object-cover transition-transform duration-200'
									/>
									{index === activeIndex && (
										<div className='absolute inset-0 bg-red-600 bg-opacity-10'></div>
									)}
								</button>
							))}
						</div>
					</div>
				)}
			</div>
		</div>
	)
}

export default ProductImageModal
